import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { GET_REQUIREMENTS, GET_STATS } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { useApp } from "@/context/AppContext";
import { useFilters } from "@/context/FilterContext";
import { TreeNode } from "@/components/TreeNode";
import { completionLabel } from "@/components/Badge";
import { ownerFilterLabel, ownerFilterOptions, ownerFilterValue } from "@/requirementFilters";
import type { Requirement } from "@/types";

export function RequirementsTree() {
  const { treeViewActive, setTreeViewActive, navigateToCreate, activeUser, activeId } = useApp();
  const { filters, setFilter, clearFilters } = useFilters();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const { data: reqsRaw, loading: isLoading } = useQuery<any>(GET_REQUIREMENTS);
  const { data: statsRaw } = useQuery<any>(GET_STATS);

  const requirements: Requirement[] = reqsRaw ? mapToSnakeCase(reqsRaw.requirements) : [];
  const stats = statsRaw ? mapToSnakeCase(statsRaw.stats) : undefined;
  const ownerOptions = ownerFilterOptions(requirements);

  const requirementDriversOf = useCallback((requirement: Requirement) => (
    requirement.requirement_drivers || []
  ).map(driver => driver.id), []);

  const drivenRequirementsOf = useCallback((driverId: string) => requirements.filter((r: Requirement) => (
    requirementDriversOf(r).includes(driverId)
  )), [requirements, requirementDriversOf]);

  const reqById = useMemo(() => new Map(requirements.map((r: Requirement) => [r.id, r])), [requirements]);
  const driverCountByReqId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const requirement of requirements) {
      counts.set(requirement.id, requirementDriversOf(requirement).length);
    }
    return counts;
  }, [requirements, requirementDriversOf]);

  const selectedDriverIds = useMemo(() => {
    if (!activeId) return new Set<string>();
    const selected = reqById.get(activeId);
    return new Set(selected ? requirementDriversOf(selected) : []);
  }, [activeId, reqById, requirementDriversOf]);

  const selectedDrivenIds = useMemo(() => {
    if (!activeId) return new Set<string>();
    return new Set(drivenRequirementsOf(activeId).map(r => r.id));
  }, [activeId, drivenRequirementsOf]);

  // Filter requirements, keeping driver requirements visible so graph structure is preserved.
  const filteredIds = useMemo(() => {
    const matching = new Set<string>();
    const strictOwnerFilter = !!filters.owner;
    requirements.forEach((r: Requirement) => {
      const isInactive = (r.active_driver_count ?? 0) === 0;
      const jiraTickets = (r.tests || []).flatMap(test => test.jira_tickets || []);
      const searchText = `${r.id} ${r.title || ""} ${r.description || ""} ${jiraTickets.join(" ")}`;
      if (filters.search && !searchText.toLowerCase().includes(filters.search.toLowerCase())) return;
      if (filters.level === "inactive") {
        if (!isInactive) return;
        matching.add(r.id);
        return;
      }
      if (isInactive) return;
      if (filters.level && r.level !== filters.level) return;
      if (filters.discipline && r.discipline !== filters.discipline) return;
      if (filters.completion && r.completion !== filters.completion) return;
      if (filters.category && r.category !== filters.category) return;
      if (filters.owner && ownerFilterValue(r.owner) !== filters.owner) return;
      if (filters.testCoverage === "has_tests" && (!r.tests || r.tests.length === 0)) return;
      if (
        filters.testCoverage === "no_tests"
        && ((r.tests?.length ?? 0) > 0 || (r.driven_child_count ?? 0) > 0)
      ) return;
      if (filters.tag === "__not_tagged__" && r.tags && r.tags.length > 0) return;
      if (filters.tag && filters.tag !== "__not_tagged__" && !(r.tags || []).includes(filters.tag)) return;
      matching.add(r.id);
    });
    if (filters.level === "inactive") return matching;

    // Include all requirement drivers of matching nodes.
    const visible = new Set(matching);
    if (!strictOwnerFilter) {
      matching.forEach(id => {
        const pending = [id];
        const seen = new Set<string>();
        while (pending.length > 0) {
          const currentId = pending.pop()!;
          if (seen.has(currentId)) continue;
          seen.add(currentId);
          const cur = reqById.get(currentId);
          if (!cur) continue;
          for (const driverId of requirementDriversOf(cur)) {
            if (visible.has(driverId)) continue;
            visible.add(driverId);
            pending.push(driverId);
          }
        }
      });
    }
    if (activeId && !strictOwnerFilter) {
      visible.add(activeId);
      for (const driverId of selectedDriverIds) visible.add(driverId);
      for (const drivenId of selectedDrivenIds) visible.add(drivenId);
    }
    return visible;
  }, [requirements, filters, requirementDriversOf, reqById, activeId, selectedDriverIds, selectedDrivenIds]);

  const expandedWithSelection = useMemo(() => {
    const expanded = new Set(expandedNodes);
    if (filters.search) {
      filteredIds.forEach(id => {
        if (drivenRequirementsOf(id).some(child => filteredIds.has(child.id))) expanded.add(id);
      });
    }
    if (!activeId) return expanded;
    const pending = [activeId];
    const seen = new Set<string>();
    while (pending.length > 0) {
      const currentId = pending.pop()!;
      if (seen.has(currentId)) continue;
      seen.add(currentId);
      const current = reqById.get(currentId);
      if (!current) continue;
      for (const driverId of requirementDriversOf(current)) {
        expanded.add(driverId);
        pending.push(driverId);
      }
    }
    return expanded;
  }, [activeId, expandedNodes, filters.search, filteredIds, reqById, requirementDriversOf, drivenRequirementsOf]);

  const hasFilters = !!(filters.search || filters.level || filters.discipline || filters.completion || filters.category || filters.owner || filters.testCoverage || filters.tag);

  const rootNodes = requirements.filter((r: Requirement) => {
    if (filters.level !== "inactive" && (r.active_driver_count ?? 0) === 0) return false;
    if (hasFilters && !filteredIds.has(r.id)) return false;
    return requirementDriversOf(r).filter(driverId => !hasFilters || filteredIds.has(driverId)).length === 0;
  });
  const childrenOf = (driverId: string) => drivenRequirementsOf(driverId).filter((r: Requirement) => !hasFilters || filteredIds.has(r.id));

  const handleToggle = useCallback((id: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = () => {
    setExpandedNodes(new Set(requirements.filter((r: Requirement) => childrenOf(r.id).length > 0).map((r: Requirement) => r.id)));
  };

  const collapseAll = () => setExpandedNodes(new Set());

  if (!treeViewActive) return null;

  const cc = stats?.completion_counts || {};

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Page header */}
      <div className="pt-5 px-7">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-text leading-tight">Requirements</h1>
          </div>
          <div className="flex items-center gap-2">
            {activeUser && activeUser.role !== "viewer" && (
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-default border text-xs font-medium cursor-pointer whitespace-nowrap transition-all duration-150 ease-in-out bg-accent text-white border-accent hover:bg-accent2 hover:border-accent2"
                onClick={() => navigateToCreate()}
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
                  <path d="M8 3v10M3 8h10" />
                </svg>
                New
              </button>
            )}
            <button className="flex items-center gap-1 px-2.5 py-1.5 border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer transition-colors duration-100 hover:border-accent hover:text-text hover:bg-bg3 [&_svg]:w-[13px] [&_svg]:h-[13px]" onClick={expandAll}>Expand All</button>
            <button className="flex items-center gap-1 px-2.5 py-1.5 border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer transition-colors duration-100 hover:border-accent hover:text-text hover:bg-bg3 [&_svg]:w-[13px] [&_svg]:h-[13px]" onClick={collapseAll}>Collapse All</button>
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-default border text-xs font-medium cursor-pointer whitespace-nowrap transition-all duration-150 ease-in-out bg-bg2 text-text2 border-border hover:bg-bg3 hover:text-text hover:border-text2"
              onClick={() => setTreeViewActive(false)}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
                <path d="M2 3h12M2 6h12M2 9h12M2 12h12" />
              </svg>
              List View
            </button>
          </div>
        </div>
        {/* Summary stats */}
        <div className="flex flex-wrap items-stretch mt-3">
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-blue leading-tight">{stats?.child_count ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-blue before:shrink-0">Total</div>
          </div>
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-green leading-tight">{cc.complete ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-green before:shrink-0">Complete</div>
          </div>
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-yellow leading-tight">{cc.partial ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-yellow before:shrink-0">Partial</div>
          </div>
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-red leading-tight">{cc.incomplete ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-red before:shrink-0">Incomplete</div>
          </div>
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-text2 leading-tight">{cc.missing_tests ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-text2 before:shrink-0">No Tests</div>
          </div>
        </div>
      </div>

      {/* Search + filters */}
      <div className="flex items-center justify-between px-7 pt-3.5 pb-2.5 gap-2">
        <div className="relative flex-1 max-w-80 min-w-40">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text2 pointer-events-none w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="6.5" cy="6.5" r="4.5" /><path d="M10 10l4 4" />
          </svg>
          <input
            type="text"
            value={filters.search}
            onChange={e => setFilter("search", e.target.value)}
            placeholder="Search requirements..."
            className="w-full py-1.5 pl-7.5 pr-2.5 bg-bg border border-border rounded-default text-text text-xs outline-none transition-colors duration-100 focus:border-accent placeholder:text-text2"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 px-7 pb-2.5 items-center [&_select]:px-2 [&_select]:py-1 [&_select]:bg-bg [&_select]:border [&_select]:border-border [&_select]:rounded-default [&_select]:text-text [&_select]:text-[11px] [&_select]:outline-none [&_select]:transition-colors [&_select]:duration-100 [&_select]:min-w-[100px] [&_select]:h-7 [&_select:focus]:border-accent">
        <select value={filters.level} onChange={e => setFilter("level", e.target.value)} aria-label="Level">
          <option value="">All levels</option>
          {(stats?.levels || []).map((l: string) => <option key={l} value={l}>{l}</option>)}
          <option value="inactive">inactive</option>
        </select>
        <select value={filters.discipline} onChange={e => setFilter("discipline", e.target.value)} aria-label="Discipline">
          <option value="">All disciplines</option>
          {(stats?.disciplines || []).map((d: string) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filters.completion} onChange={e => setFilter("completion", e.target.value)} aria-label="Status">
          <option value="">All statuses</option>
          {Object.keys(cc).map(s => <option key={s} value={s}>{completionLabel(s)}</option>)}
        </select>
        <select value={filters.category} onChange={e => setFilter("category", e.target.value)} aria-label="Category">
          <option value="">All categories</option>
          {(stats?.categories || []).map((c: string) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.owner} onChange={e => setFilter("owner", e.target.value)} aria-label="Owner">
          <option value="">All owners</option>
          {ownerOptions.map((owner: string) => <option key={owner} value={owner}>{ownerFilterLabel(owner)}</option>)}
        </select>
        <select value={filters.testCoverage} onChange={e => setFilter("testCoverage", e.target.value)} aria-label="Test Coverage">
          <option value="">All coverage</option>
          <option value="has_tests">Has tests</option>
          <option value="no_tests">No tests</option>
        </select>
        <select value={filters.tag} onChange={e => setFilter("tag", e.target.value)} aria-label="Tag">
          <option value="">All tags</option>
          <option value="__not_tagged__">Not tagged</option>
          {(stats?.tags || []).map((t: string) => <option key={t} value={t}>{t}</option>)}
        </select>
        {(filters.level || filters.discipline || filters.completion || filters.category || filters.owner || filters.testCoverage || filters.tag) && (
          <button className="px-2.5 py-1 bg-transparent border-none text-text2 text-[11px] cursor-pointer transition-colors duration-100 ml-auto hover:text-accent" onClick={clearFilters}>Clear filters</button>
        )}
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-auto min-h-0">
        {/* Column headers */}
        <div className="flex items-center pl-4 pr-6 py-1.5 border-b border-border text-[10px] text-text2 uppercase tracking-wide font-semibold sticky top-0 bg-bg z-10">
          <div className="flex-1" />
          <div className="flex shrink-0">
            <div className="w-[90px] text-right px-1">Level</div>
            <div className="w-[80px] text-right px-1">Discipline</div>
            <div className="w-[80px] text-right px-1">Status</div>
            <div className="w-[100px] text-right px-1">Tags</div>
          </div>
        </div>
        <div className="px-4 py-2">
        {isLoading ? (
          <div className="text-center p-8 text-text2">Loading tree...</div>
        ) : rootNodes.length === 0 ? (
          <div className="text-center p-8 text-text2">No requirements found</div>
        ) : (
          rootNodes.map(req => (
            <TreeNode
              key={req.id}
              requirement={req}
              children={childrenOf(req.id)}
              allRequirements={requirements}
              getChildren={childrenOf}
              expandedNodes={expandedWithSelection}
              onToggle={handleToggle}
              selectedId={activeId}
              highlightDriverIds={selectedDriverIds}
              highlightDrivenIds={selectedDrivenIds}
              driverCountByReqId={driverCountByReqId}
            />
          ))
        )}
        </div>
      </div>
    </div>
  );
}
